import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Angle } from 'tramlib';
import { ServicesConfig } from '../services/services.config';
import {
  EasyTramPlan,
  Field,
  Polygon,
  Scenario,
  ScenarioBorder,
  ScenarioBorderRing,
  ScenarioBorderSegment,
} from './field.model';
import { createNewEasyTramPlanFromField } from '../map/easyTram/map-drawing-logic-easy-tram';

@Injectable({
  providedIn: 'root',
})
export class FieldsService {
  constructor(private http: HttpClient) {}

  getFields(): Observable<Field[]> {
    return this.http.get<Field[]>(ServicesConfig.fieldsServiceUrl).pipe(
      map((fields) => {
        return fields.map((f) => this.normalizeField(f));
      })
    );
  }

  normalizeField(f: any): Field {
    const newField = new Field();
    newField.id = f.id;
    newField.name = f.name;
    newField.area = f.area;
    newField.type = f.type;
    newField.polygon = f.polygon;
    newField.isSelected = f.isSelected;
    newField.customerId = f.customerId;
    newField.scenarios = f.scenarios;
    newField.easyTramPlans = f.easytramPlans

    newField.createdAt = f.createdAt;
    newField.createdBy = f.createdBy;
    newField.modifiedAt = f.modifiedAt;
    newField.modifiedBy = f.modifiedBy;
    newField.deletedAt = f.deletedAt;
    newField.deletedBy = f.deletedBy;

    newField.newField = f.newField;

    if (newField.scenarios !== undefined) {
      newField.scenarios = newField.scenarios.map((s) =>
        this.normalizeScenario(s)
      );
    }
    if (newField.easyTramPlans !== undefined) {
      newField.easyTramPlans = newField.easyTramPlans.map((etp) =>
        this.normalizeEasyTramPlan(etp, newField)
      );
    }
    if (newField.polygon) {
      newField.polygon = new Polygon(
        newField.polygon.coordinates,
        newField.polygon.type
      );
    }
    return newField;
  }

  normalizeEasyTramPlan(etp: any, field: Field): EasyTramPlan {
    const easyTramPlan = createNewEasyTramPlanFromField(field);

    easyTramPlan.id = etp.id;
    easyTramPlan.name = etp.name;
    easyTramPlan.createdAt = etp.createdAt;
    easyTramPlan.createdBy = etp.createdBy;

    easyTramPlan.angleMax = etp.planData.settings.angleMax;
    easyTramPlan.speedMax = etp.planData.settings.speedMax;
    easyTramPlan.overlap = etp.planData.settings.overlap;

    easyTramPlan.workingWidthCareDevice = etp.planData.settings.plantCareWorkingWidth;
    easyTramPlan.trackWidthCareDevice = etp.planData.settings.wheelTrack;
    easyTramPlan.wheelWidthCareDevice = etp.planData.settings.tireWidth;

    easyTramPlan.headlandType = etp.planData.settings.headlandMode;
    easyTramPlan.headlandWidth = etp.planData.settings.headlandWidth;
    easyTramPlan.withTramlines = etp.planData.headland !== undefined;
    easyTramPlan.degreeOffset = Angle.from_radian(etp.planData.settings.coverageLineOffsetAngle).as_degree();
    easyTramPlan.shiftOffset = etp.planData.settings.coverageLineOffset;
    easyTramPlan.corelandBaseSegment = etp.planData.settings.mainlandGuidanceReference;

    for (const segment of easyTramPlan.border.outer.segments) {
      if (
        segment.coordinates[0][0] === easyTramPlan.corelandBaseSegment?.coordinates[0][0]
        && segment.coordinates[0][1] === easyTramPlan.corelandBaseSegment?.coordinates[0][1]
        && segment.coordinates[1][0] === easyTramPlan.corelandBaseSegment?.coordinates[1][0]
        && segment.coordinates[1][1] === easyTramPlan.corelandBaseSegment?.coordinates[1][1]
      ) {
        segment.easyTramSegmentType = 'mainlandBaseSegment';
      }
    }

    return easyTramPlan;
  }

  normalizeScenario(s: Scenario): Scenario {
    const newScenario = new Scenario();
    newScenario.id = s.id;
    newScenario.createdAt = s.createdAt;
    newScenario.createdBy = s.createdBy;
    newScenario.deletedAt = s.deletedAt;
    newScenario.deletedBy = s.deletedBy;
    newScenario.modifiedAt = s.modifiedAt;
    newScenario.modifiedBy = s.modifiedBy;
    newScenario.name = s.name;
    newScenario.state = s.state;
    newScenario.border = this.normalizeScenarioBorder(s.border);

    return newScenario;
  }

  normalizeScenarioBorder(sb: ScenarioBorder): ScenarioBorder {
    const newSB = new ScenarioBorder();
    newSB.inners = sb.inners?.map((ring) =>
      this.normalizeScenarioBorderRing(ring)
    );
    newSB.outer = this.normalizeScenarioBorderRing(sb.outer);
    return newSB;
  }

  normalizeScenarioBorderRing(sbr: ScenarioBorderRing): ScenarioBorderRing {
    const newSBR = new ScenarioBorderRing();
    if (sbr != undefined) {
      newSBR.segments = sbr.segments.map((segment) =>
        this.normalizeScenarioBorderSegment(segment)
      );
    }
    return newSBR;
  }

  normalizeScenarioBorderSegment(
    sbs: ScenarioBorderSegment
  ): ScenarioBorderSegment {
    const newSBS = new ScenarioBorderSegment();
    if (sbs != undefined) {
      newSBS.coordinates = sbs.coordinates;
      newSBS.infacing = sbs.infacing;
      newSBS.outfacing = sbs.outfacing;
    }
    return newSBS;
  }

  getField(fieldId: string): Observable<Field> {
    return this.http.get<Field>(
      ServicesConfig.fieldsServiceUrl + '/' + fieldId
    );
  }

  createField(field: Field): Observable<Field> {
    return this.http.post<Field>(
      ServicesConfig.fieldsServiceUrl,
      JSON.stringify(field)
    );
  }
  updateField(id: string, field: Field): Observable<Field> {
    const newField = { ...field };
    newField.id = undefined; //unset ID as this is not allowed to get patched
    return this.http.patch<Field>(
      ServicesConfig.fieldsServiceUrl + '/' + id,
      JSON.stringify(newField)
    );
  }
  deleteField(field: Field): Observable<void> {
    return this.http.delete<void>(
      ServicesConfig.fieldsServiceUrl + '/' + field.id
    );
  }

  createScenario(field: Field, scenario: Scenario): Observable<Field> {
    return this.http.post<Field>(
      ServicesConfig.fieldsServiceUrl + '/' + field.id + '/scenarios',
      JSON.stringify(scenario)
    );
  }
  updateScenario(field: Field, scenario: Scenario): Observable<Field> {
    const newScenarioObj = JSON.parse(JSON.stringify(scenario)); // create a copy to let the id remain a mandatory field in the original object
    delete newScenarioObj.id;
    return this.http.patch<Field>(
      ServicesConfig.fieldsServiceUrl +
        '/' +
        field.id +
        '/scenarios/' +
        scenario.id,
      JSON.stringify(newScenarioObj)
    );
  }
  deleteScenario(field: Field, scenario: Scenario): Observable<void> {
    return this.http.delete<void>(
      ServicesConfig.fieldsServiceUrl +
        '/' +
        field.id +
        '/scenarios/' +
        scenario.id
    );
  }
  exportAllScenarios(): Observable<Blob> {
    return this.http.get<Blob>(
      ServicesConfig.fieldsServiceUrl + '/scenarios/export',
      { responseType: 'blob' as 'json' }
    );
  }
  exportScenarios(scenarios: Scenario[]): Observable<Blob> {
    let scenarioIds: string[] = [];
    scenarios.forEach((scenario) => scenarioIds.push(scenario!.id!));

    return this.http.post<Blob>(
      ServicesConfig.fieldsServiceUrl + '/scenarios/export',
      JSON.stringify(scenarioIds),
      { responseType: 'blob' as 'json' }
    );
  }

  createEasyTramPlan(field: Field, newPlan: EasyTramPlan): Observable<Field> {
    const easyTramPlan = newPlan.clone();

    const planData: any = easyTramPlan.planData;
    planData.settings.angleMax = easyTramPlan.angleMax;
    planData.settings.speedMax = easyTramPlan.speedMax;
    planData.settings.overlap = easyTramPlan.overlap;
    planData.settings.mainlandGuidanceReference = easyTramPlan.corelandBaseSegment;

    return this.http.post<Field>(
      ServicesConfig.fieldsServiceUrl + '/' + field.id + '/easytram',
      JSON.stringify({
        id: easyTramPlan.id,
        name: easyTramPlan.name,
        border: easyTramPlan.border,
        state: easyTramPlan.state,
        planData,
        createdBy: easyTramPlan.createdBy,
        createdAt: easyTramPlan.createdAt,
        modifiedBy: easyTramPlan.modifiedBy,
        modifiedAt: easyTramPlan.modifiedAt,
        deletedBy: easyTramPlan.deletedBy,
        deletedAt: easyTramPlan.deletedAt,
      })
    );
  }

  updateEasyTramPlan(field: Field, newPlan: EasyTramPlan): Observable<Field> {
    const easyTramPlan = newPlan.clone();

    const planData: any = easyTramPlan.planData;
    planData.settings.angleMax = easyTramPlan.angleMax;
    planData.settings.speedMax = easyTramPlan.speedMax;
    planData.settings.overlap = easyTramPlan.overlap;
    planData.settings.mainlandGuidanceReference = easyTramPlan.corelandBaseSegment;

    return this.http.patch<Field>(
      ServicesConfig.fieldsServiceUrl +
        '/' +
        field.id +
        '/easytram/' +
        easyTramPlan.id,
      JSON.stringify({
        name: easyTramPlan.name,
        planData,
      })
    );
  }
}
